import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The typed section map for a comic — two sections, both hand-written, and the last
 * hand-transcription this client needs.
 *
 * Same story as the four domains before it: `ComicSheetDto.Sections` is
 * `IReadOnlyDictionary<string, object>` on the wire, and nothing in the API references
 * `ComicCoreSectionDto` or `ComicScenesSectionDto` from an operation, so OpenAPI never
 * sees them. Transcribed from `CampaignManager.Api/Contracts/Comics/ComicContracts.cs`.
 *
 * `ComicSummaryDto` *is* generated — it is the return type of the list endpoint, so it is
 * reachable and the drift check covers it. So is `GalleryImageDto`, which is why a page's
 * images below are the generated type rather than a fifth transcription.
 */
export interface ComicSections {
  core: ComicCoreSection
  scenes: ComicScenesSection
}

/**
 * Comic identity plus the counts and rule flags a client would otherwise derive.
 * Section key `core`.
 *
 * `hasFrontCover` / `hasBackCover` are the interesting pair. A comic may hold at most one
 * of each **across all its scenes**, and a second is a `409` — so the flags ship on `core`
 * for the reason every other server-computed flag does: the client greys the option out
 * rather than discovering the rule by being refused.
 */
export interface ComicCoreSection {
  id: number
  campaignId: number
  name: string
  caseId: number
  caseName: string | null
  sceneCount: number
  pageCount: number
  hasFrontCover: boolean
  hasBackCover: boolean
  createdAt: string
}

/** What kind of page this is. Two of the three are unique per comic. */
export type PageType = Schema['PageType']

/** A page of the comic, with its script and its images. */
export interface ComicPage {
  id: number
  type: PageType
  order: number
  script: string
  images: Schema['GalleryImageDto'][]
  createdAt: string
}

/**
 * One scene of the comic and its pages, in order.
 *
 * `sceneId` is the case scene this dramatizes; `sceneTitle` rides alongside it so
 * rendering a comic needs no lookup into the case sheet — the same "the client derives
 * nothing" rule that puts `caseName` on `core`.
 */
export interface ComicScene {
  id: number
  name: string
  order: number
  sceneId: number
  sceneTitle: string | null
  pages: ComicPage[]
}

/** The comic's scenes, in order. Section key `scenes`. */
export interface ComicScenesSection {
  scenes: ComicScene[]
}

export type ComicSummary = Schema['ComicSummaryDto']

export interface ComicSheet {
  comicId: number
  campaignId: number
  version: number
  /** Whether this caller may write. Members read; the Game Master writes. */
  canManage: boolean
  sections: Partial<ComicSections>
}

export function toComicSheet(dto: Schema['ComicSheetDto']): ComicSheet {
  if (dto.comicId === undefined) {
    throw new Error('The API returned a comic sheet with no id.')
  }

  return {
    comicId: dto.comicId,
    campaignId: dto.campaignId ?? 0,
    version: dto.version ?? 0,
    canManage: dto.canManage ?? false,
    sections: (dto.sections ?? {}) as Partial<ComicSections>,
  }
}

/** The three page kinds, in the order a comic runs. */
export const PAGE_TYPES: PageType[] = ['FrontCover', 'Page', 'BackCover']

const PAGE_TYPE_LABELS: Record<PageType, string> = {
  FrontCover: 'Front cover',
  Page: 'Page',
  BackCover: 'Back cover',
}

export const pageTypeLabel = (type: PageType): string => PAGE_TYPE_LABELS[type] ?? type

/**
 * Whether a page kind can still be added to this comic.
 *
 * The rule is the server's and the flags are the server's; this only reads them. A comic
 * holds at most one front cover and one back cover across *all* its scenes, so those two
 * options disappear once taken instead of being offered and refused.
 */
export function canAddPageType(core: ComicCoreSection | undefined, type: PageType): boolean {
  if (core === undefined) return type === 'Page'
  if (type === 'FrontCover') return !core.hasFrontCover
  if (type === 'BackCover') return !core.hasBackCover
  return true
}

/** The page kinds still available on this comic. Never empty — `Page` is always one. */
export function availablePageTypes(core: ComicCoreSection | undefined): PageType[] {
  return PAGE_TYPES.filter((type) => canAddPageType(core, type))
}

/**
 * The image a page row shows, at the smallest size that will do.
 *
 * A page may carry several images; the first is the one the row leads with, and the rest
 * are reachable in the page's own list. `isMain` means nothing here — the comic page has
 * no main image concept, only an order.
 */
export function leadImage(page: ComicPage): Schema['GalleryImageDto'] | undefined {
  return page.images[0]
}
